import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { CheckCircleIcon, PauseCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';

export default function HOAApprovalPage() {
  const { user } = useAuth();
  const [gyms, setGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [decision, setDecision] = useState('');
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchPending(); }, []);

  const fetchPending = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/hoa-approval/pending');
      setGyms(res.data);
    } catch {
      toast.error('Failed to load pending gyms');
    } finally {
      setLoading(false);
    }
  };

  const openDecisionModal = (gym, dec) => {
    setSelected(gym);
    setDecision(dec);
    setRemarks('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (remarks.trim().length < 5) {
      toast.error('Remarks must be at least 5 characters');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`/api/hoa-approval/${selected.id}/decide`, { decision, remarks });
      const label = decision === 'approve' ? 'Approved — moved to Proposal Sent' :
                    decision === 'hold' ? 'Gym held for review' : 'Gym rejected';
      toast.success(label);
      setSelected(null);
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to process decision');
    } finally {
      setSubmitting(false);
    }
  };

  const decisionConfig = {
    approve: { label: 'Approve', color: 'bg-green-600 hover:bg-green-700', icon: CheckCircleIcon, hint: 'Gym will move to Proposal Sent stage' },
    hold:    { label: 'Hold',    color: 'bg-yellow-500 hover:bg-yellow-600', icon: PauseCircleIcon, hint: 'Gym stays in Internal Approval — add reason for hold' },
    reject:  { label: 'Reject',  color: 'bg-red-600 hover:bg-red-700', icon: XCircleIcon, hint: 'Gym will be moved to Expired Clients — this cannot be undone easily' },
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">HOA Gym Approvals</h1>
          <p className="text-gray-500 text-sm mt-1">
            Review gyms submitted for approval. Every decision requires a remark.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Pending Review</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{gyms.length}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Your Role</p>
            <p className="text-sm font-semibold text-gray-700 mt-1">{user?.role?.name || 'Head of Acquisition'}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Required</p>
            <p className="text-sm font-semibold text-gray-700 mt-1">Remarks on every decision</p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-400">Loading pending gyms...</div>
        ) : gyms.length === 0 ? (
          <div className="text-center py-20">
            <CheckCircleIcon className="h-12 w-12 text-green-400 mx-auto mb-3" />
            <p className="text-gray-500">No gyms pending HOA approval right now.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {gyms.map(gym => (
              <GymApprovalCard
                key={gym.id}
                gym={gym}
                onDecide={(dec) => openDecisionModal(gym, dec)}
              />
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            {(() => {
              const cfg = decisionConfig[decision];
              const Icon = cfg.icon;
              return (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <Icon className="h-6 w-6 text-gray-700" />
                    <h2 className="text-lg font-semibold text-gray-900">{cfg.label} — {selected.name}</h2>
                  </div>
                  <p className="text-sm text-gray-500 mb-4 bg-gray-50 px-3 py-2 rounded-lg">{cfg.hint}</p>
                  <form onSubmit={handleSubmit}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Remarks <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={4}
                      value={remarks}
                      onChange={e => setRemarks(e.target.value)}
                      placeholder="Add your reasoning, observations, or conditions..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      required
                      minLength={5}
                    />
                    <p className="text-xs text-gray-400 mt-1">{remarks.length} chars (min 5)</p>
                    <div className="flex gap-3 mt-4">
                      <button
                        type="submit"
                        disabled={submitting || remarks.trim().length < 5}
                        className={`flex-1 text-white text-sm font-medium py-2 rounded-lg ${cfg.color} disabled:opacity-50`}
                      >
                        {submitting ? 'Processing...' : `Confirm ${cfg.label}`}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelected(null)}
                        className="flex-1 border border-gray-300 text-sm font-medium py-2 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

function GymApprovalCard({ gym, onDecide }) {
  const visits = gym.visitLogs?.length || 0;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900">{gym.name}</h3>
          <p className="text-sm text-gray-500 mt-0.5">{gym.address}, {gym.city}</p>
          {gym.assignedUser && (
            <p className="text-xs text-gray-400 mt-1">BD: {gym.assignedUser.name}</p>
          )}
        </div>
        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-medium">
          Pending
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 bg-gray-50 rounded-lg p-3">
        <Metric label="Site Visits" value={visits} />
        <Metric label="Sqft" value={gym.totalSqft ? `${gym.totalSqft.toLocaleString('en-IN')}` : '—'} />
        <Metric label="Active Clients" value={gym.activeClients ?? '—'} />
        <Metric label="Monthly Rent" value={gym.monthlyRent ? `₹${gym.monthlyRent.toLocaleString('en-IN')}` : '—'} />
      </div>

      {gym.hoaDecision === 'hold' && gym.hoaRemarks && (
        <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
          <span className="font-medium">Previous hold note:</span> {gym.hoaRemarks}
        </div>
      )}

      <div className="flex gap-2 mt-4">
        <button onClick={() => onDecide('approve')}
          className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg">
          <CheckCircleIcon className="h-4 w-4" /> Approve
        </button>
        <button onClick={() => onDecide('hold')}
          className="flex items-center gap-1.5 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium px-4 py-2 rounded-lg">
          <PauseCircleIcon className="h-4 w-4" /> Hold
        </button>
        <button onClick={() => onDecide('reject')}
          className="flex items-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 text-sm font-medium px-4 py-2 rounded-lg">
          <XCircleIcon className="h-4 w-4" /> Reject
        </button>
        <a href={`/gyms/${gym.id}`} target="_blank" rel="noopener noreferrer"
          className="ml-auto text-sm text-gray-500 hover:text-gray-700 underline">
          View Details
        </a>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm font-semibold text-gray-700">{value}</p>
    </div>
  );
}
